import { ReactElement, SyntheticEvent, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useAtom } from 'jotai';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import InstantBookingIcon from './images/instant-booking-22x22-blue.svg';
import InstantBookingReverseIcon from './images/instant-book-reverse.svg';
import { dollarsNoCentsWithSymbol } from '../../../../helpers/currency';
import { hoveredBoatIdAtom } from '../Filter/filterStore';
import { Flex, FlexCenterBoth, FlexCol } from '../../../primitives/flex';
import { Text } from '../../../primitives/typography';
import { cloudFlareLoader } from '../../../../utils/images';
import Badge from '../Badge';
import StarRating from '../StarRating';
import { useBreakpoint } from '../../../BreakpointProvider';

export interface SearchMapMarkerProps {
  onClick?: (e: SyntheticEvent) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  lat: number;
  // eslint-disable-next-line react/no-unused-prop-types
  lng: number;
  boat: any;
  className?: string;
  url: string;
  showDetail: boolean;
  setClickedID: (any) => any;
}

export default function SearchMapMarker(props: SearchMapMarkerProps): ReactElement {
  const { boat, className, showDetail, setClickedID, url, onClick } = props;
  const [hoveredBoatId, setHoveredBoatId] = useAtom(hoveredBoatIdAtom);
  const markerRef = useRef(null);
  const detailRef = useRef(null);

  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;

  const router = useRouter();

  const openPDP = () => {
    if (smallScreen) router.push(url);
    else window.open(url);
  };

  const handleClick = (e: SyntheticEvent) => {
    if (onClick) {
      onClick(e);
    }
  };

  useOnClickOutside(detailRef, (e) => {
    if (markerRef.current) {
      const parentMarkerElement = e.target.closest(`.${markerRef.current.className.split(' ').join('.')}`);
      if (showDetail && !parentMarkerElement) {
        setClickedID(null);
      }
    }
  });

  return (
    <RelWrapper ref={markerRef}>
      <MarkerWrapper
        hovered={hoveredBoatId === boat.boat_public_id}
        onMouseEnter={() => setHoveredBoatId(boat.boat_public_id)}
        onMouseLeave={() => setHoveredBoatId(null)}
        onClick={handleClick}
        data-testid="search-map-boat-pin-marker"
      >
        {boat.instant_bookable && (
          <Icon>
            <InstantBookingIcon data-testid="search-map-boat-pin-ib-marker" />
          </Icon>
        )}
        <Price className={className} data-testid="search-map-boat-pin-price-marker">
          {dollarsNoCentsWithSymbol(boat.price_per_hour_cents)}
        </Price>
      </MarkerWrapper>

      <Detail ref={detailRef} onClick={openPDP} showDetail={showDetail} data-testid="search-map-boat-pin-detail-box">
        <div onClick={openPDP} data-testid="search-map-boat-pin-detail-photo-box">
          {boat.instant_bookable && (
            <IconWrapper>
              <InstantBookingReverseIcon />
            </IconWrapper>
          )}
          <BoatImage
            loader={cloudFlareLoader as any}
            width={100}
            height={100}
            src={new URL(boat.photos?.[0]?.standard?.url)?.pathname.slice(1)}
            alt={boat.title}
            style={{
              objectFit: 'cover',
            }}
          />
        </div>
        <InfoWrapper data-testid="search-map-boat-pin-detail-info-box">
          <Title
            fWeight={600}
            fSize="14px"
            onClick={openPDP}
            data-testid="search-map-boat-pin-detail-info-box-top-line"
          >
            {boat.title}
          </Title>
          <Flex width="100%" pt="5px" gap="5px" data-testid="search-map-boat-pin-detail-info-box-middle-line">
            {boat.is_top_owner && (
              <div>
                <Badge small topOwner />
              </div>
            )}
            {boat.rating > 0 && (
              <Flex>
                <StarRating
                  rating={boat.rating}
                  reviewCount={boat.completed_booking_count}
                  countFontSize="10px"
                  ratingFontSize="10px"
                  elementsGap="2px"
                  singleStar
                />
              </Flex>
            )}
          </Flex>
          <TextWrapper mt="auto" data-testid="search-map-boat-pin-detail-into-box-bottom-line">
            <Text fWeight={700} fSize="14px">
              {boat.display_price_per_hour}
            </Text>
            <Text fSize="10px" fWeight="500" ml="2px" mt="2px">
              /hour
            </Text>
          </TextWrapper>
        </InfoWrapper>
      </Detail>
    </RelWrapper>
  );
}

const TextWrapper = styled(Flex)``;

const RelWrapper = styled.div`
  position: relative;
`;

const Title = styled(Text)`
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
  /* max-width: 170px; */
  text-overflow: ellipsis;
`;

const BoatImage = styled(Image)`
  top: 0;
  border-bottom-left-radius: 8px;
  border-top-left-radius: 8px;
`;

const IconWrapper = styled(FlexCenterBoth)`
  background: #0a4195;
  position: absolute;
  z-index: 1;
  top: 6px;
  left: -8px;
  border-radius: 4px;
  width: 25px;
  height: 25px;
`;

const Detail = styled(Flex)<{ showDetail: boolean }>`
  position: absolute;
  top: -120px;
  transform: translateX(-50%);
  width: 300px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  height: 100px;
  color: #17233c !important;
  z-index: 2;
  cursor: pointer;
  pointer-events: all;
  background: white;
  display: none;

  ${({ showDetail }) =>
    showDetail &&
    css`
      display: flex;
    `}
`;

const InfoWrapper = styled(FlexCol)`
  padding: 14px 10px 20px;
  font-family: 'AvenirNext', 'Avenir', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;

const MarkerWrapper = styled.div<{ hovered: boolean }>`
  max-width: fit-content;
  min-width: fit-content;
  background: #ffffff;
  border: 0.5px solid #c1c1c1;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.11);
  border-radius: 4px;
  padding: 4px;
  top: 50%;
  left: 50%;
  transform: translateX(-50%);

  position: relative;
  display: flex;
  align-items: center;
  text-align: left;
  cursor: ${(props) => (props.onClick ? 'pointer' : 'default')};
  &:hover {
    z-index: 1;
    transform: translateX(-50%) scale(1.2);
  }

  ${({ hovered }) =>
    hovered &&
    css`
      z-index: 1;
      transform: translateX(-50%) scale(1.2);
    `}
`;

const Price = styled.div`
  font-family: 'AvenirNext', 'Avenir', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-style: normal;
  font-weight: 900;
  font-size: 13px;
  line-height: 20px;
`;

const Icon = styled.div`
  height: 18px;
  padding-right: 4px;
  svg {
    width: 18px;
    height: 18px;
  }
`;
